// src/pages/ETFLettersPage.js
//
// v3.5.99.7.18 — ETF Letters list view (Phase B of the ETF tracking arc).
// Renders the etf_letters table built in v7.17 as a filterable, sortable
// list with inline-expandable detail rows.
//
// v3.5.99.7.19 — Phase C. Adds:
//   - Inline edit + regenerate for unsent letters (status pdf_generated/draft)
//   - Void action for any non-voided letter, with auto-cancel of any active
//     SignWell document
//   - Void & Create New shortcut on locked letters — voids the current row,
//     then navigates to /etf-calculator with the values pre-filled and the
//     new row linked back via superseded_from
//   - Letter UID (ETF-xxxxxxxx) shown in the row + detail panel
//   - Edit/Void/Void & Create New actions live in the inline detail panel
//     (no separate route — keeps the list scrollable while editing)
//
// Layout:
//   Header   — title + count + "+ New ETF Letter"
//   Filters  — status chips (multi-select), date range presets, client search
//   Table    — date, client/ref + UID, amount, status badge, last event, expand
//   Expanded — full ETF inputs (or edit form), event timeline, action buttons

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { dealUrlFor } from '../lib/hubspot';
import { calculateEtf, buildEtfLetterHTML } from '../lib/etfCalculator';
import { EtfCalculatorBody } from '../components/ETFCalculatorModal';
import { v3 } from '../lib/styleTokens';

// ── Status visual config (mirrors QuotesPage.STATUS_STYLE shape) ──────────
// v3.7.6: colors mapped to the v3 status token palette (same hexes the rest of
// the app's status pills use). Labels + statuses unchanged — the status state
// machine below is untouched.
const STATUS_STYLE = {
  pdf_generated: { bg: v3.color.status.grayBg,  color: v3.color.status.grayText,  label: 'Generated' },
  sent:          { bg: v3.color.status.blueBg,  color: v3.color.status.blueText,  label: 'Sent' },
  viewed:        { bg: v3.color.status.amberBg, color: v3.color.status.amberText, label: 'Viewed' },
  signed:        { bg: v3.color.status.greenBg, color: v3.color.status.greenText, label: 'Signed' },
  declined:      { bg: v3.color.status.redBg,   color: v3.color.status.redText,   label: 'Declined' },
  expired:       { bg: v3.color.status.grayBg,  color: v3.color.mutedSoft,        label: 'Expired' },
  voided:        { bg: v3.color.status.grayBg,  color: v3.color.mutedSoft,        label: 'Voided' },
  draft:         { bg: v3.color.status.grayBg,  color: v3.color.status.grayText,  label: 'Draft' },
};

// Status filter chips — in narrative order
const STATUS_CHIPS = ['pdf_generated', 'sent', 'viewed', 'signed', 'declined', 'expired', 'voided'];

// Date range presets
const DATE_RANGES = [
  { key: 'all',  label: 'All time',  days: null },
  { key: '7',    label: 'Last 7d',   days: 7 },
  { key: '30',   label: 'Last 30d',  days: 30 },
  { key: '90',   label: 'Last 90d',  days: 90 },
];

const fmt$ = (n) => {
  if (n == null || n === '' || isNaN(Number(n))) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fmtDateShort = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' · ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

// v3.5.99.7.19: Build the short letter ref shown on the PDF and in the list.
// First 8 chars of the UUID prefixed with "ETF-". Stable for the life of the
// row — survives edits-in-place since those don't change the id.
function letterRefFor(row) {
  if (!row?.id) return '';
  return `ETF-${String(row.id).slice(0, 8)}`;
}

// Status determines what actions are available in the detail panel:
//   - pdf_generated, draft → editable in place (Edit + Void)
//   - sent, viewed         → locked, SignWell doc active (Void & Create New)
//   - declined, expired    → locked, SignWell doc terminal (Void & Create New)
//   - signed, voided       → fully terminal (no actions, view only)
function isEditable(row) {
  return row && (row.status === 'pdf_generated' || row.status === 'draft');
}
function canVoidAndReplace(row) {
  return row && ['sent', 'viewed', 'declined', 'expired'].includes(row.status);
}
function canVoidOnly(row) {
  // Anything not terminal can be voided. 'signed' is fully locked (we don't
  // void completed contracts). 'voided' is already voided.
  return row && row.status !== 'signed' && row.status !== 'voided';
}

// Pick the most recent meaningful event timestamp + label for the "Last event" column
function lastEvent(row) {
  const candidates = [
    { ts: row.signwell_signed_at,   label: 'Signed' },
    { ts: row.signwell_declined_at, label: 'Declined' },
    { ts: row.signwell_expired_at,  label: 'Expired' },
    { ts: row.signwell_viewed_at,   label: 'Viewed' },
    { ts: row.signwell_sent_at,     label: 'Sent' },
    { ts: row.pdf_generated_at,     label: 'Generated' },
    { ts: row.created_at,           label: 'Created' },
  ];
  for (const c of candidates) {
    if (c.ts) return { ts: c.ts, label: c.label };
  }
  return { ts: row.created_at, label: 'Created' };
}


export default function ETFLettersPage() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  // Filter state
  const [statusFilter, setStatusFilter] = useState(new Set()); // empty = show all
  const [dateRange,    setDateRange]    = useState('all');
  const [search,       setSearch]       = useState('');
  const [expandedId,   setExpandedId]   = useState(null);

  // v3.5.99.7.19: edit mode state. editingId is the id of the row currently
  // being edited inline (null = no edit in progress). actionStatus is a
  // per-row transient string for surfacing save/void results in the panel.
  const [editingId,    setEditingId]    = useState(null);
  const [actionStatus, setActionStatus] = useState({}); // { [rowId]: 'message' }
  const [actionBusy,   setActionBusy]   = useState({}); // { [rowId]: true }

  async function load() {
    setLoading(true);
    setError('');
    let q = supabase.from('etf_letters').select('*').order('created_at', { ascending: false });
    // Server-side date filter (cheaper than client-side for large tables)
    const range = DATE_RANGES.find(r => r.key === dateRange);
    if (range?.days) {
      const cutoff = new Date(Date.now() - range.days * 86400000).toISOString();
      q = q.gte('created_at', cutoff);
    }
    const { data, error: err } = await q.limit(500);
    if (err) {
      console.error('ETF letters load error:', err);
      setError(err.message || 'Failed to load ETF letters');
      setRows([]);
    } else {
      setRows(data || []);
    }
    setLoading(false);
  }

  useEffect(() => { load(); /* eslint-disable-line */ }, [dateRange]);

  // ── v3.5.99.7.19: action handlers ──────────────────────────────────────

  // Re-render PDF for an existing pdf_generated/draft row with updated values.
  // Same row id, same letter ref — only the content changes.
  async function handleSaveEdit(row, edits) {
    const id = row.id;
    setActionBusy(p => ({ ...p, [id]: true }));
    setActionStatus(p => ({ ...p, [id]: '' }));
    try {
      const ferrumInfo = await loadFerrumInfo();
      const calc = calculateEtf({
        mrr: edits.mrr,
        term: edits.term,
        contractStart: edits.contractStart ? new Date(edits.contractStart) : null,
        terminationDate: edits.terminationDate ? new Date(edits.terminationDate) : null,
      });
      const letterRef = letterRefFor(row);
      const html = buildEtfLetterHTML({
        clientName: edits.clientName,
        clientAddress: edits.clientAddress,
        mrr: Number(edits.mrr),
        term: parseInt(edits.term, 10),
        contractStart: new Date(edits.contractStart),
        terminationDate: new Date(edits.terminationDate),
        ferrumInfo,
        signatureMode: 'none',
        contractReference: edits.contractReference,
        letterRef,
      });
      const safeBiz = (edits.clientName || 'Client').replace(/[^a-z0-9]+/gi, '_').slice(0, 40);
      const filename = `ETF_Letter_${safeBiz}_${letterRef}.pdf`;
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'regenerate_in_place',
          payload: {
            letterId: id,
            html, filename,
            entityName: edits.clientName,
            etfAmount: calc.etfAmount,
            contractReference: edits.contractReference,
            mrr: Number(edits.mrr),
            termMonths: parseInt(edits.term, 10),
            contractStartDate: edits.contractStart || null,
            terminationDate: edits.terminationDate || null,
            monthsElapsed: calc.monthsElapsed,
            monthsRemaining: calc.monthsRemaining,
            clientAddress: edits.clientAddress || null,
            clientContactName: edits.clientContact || null,
            clientContactEmail: edits.clientEmail || null,
            calculatedBy: profile?.full_name || profile?.email || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.pdf_base64) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      // Trigger fresh download so the rep has the regenerated PDF locally
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${data.pdf_base64}`;
      link.download = data.filename || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setActionStatus(p => ({ ...p, [id]: `✓ Saved & regenerated (${letterRef})${data.hubspot?.ok ? ' · re-uploaded to HubSpot' : ''}` }));
      setEditingId(null);
      await load();  // refresh row data after PATCH
    } catch (e) {
      console.error('ETF regenerate failed:', e);
      setActionStatus(p => ({ ...p, [id]: '✗ ' + (e.message || 'Save failed') }));
    } finally {
      setActionBusy(p => ({ ...p, [id]: false }));
    }
  }

  // Void a letter. If active in SignWell, cancels there too.
  async function handleVoid(row, voidReason) {
    const id = row.id;
    if (!window.confirm(`Void this ETF letter for ${row.client_name || 'this client'}?\n\nLetter: ${letterRefFor(row)}\nStatus: ${row.status}\n\n${row.signwell_doc_id && ['sent', 'viewed'].includes(row.status) ? 'The active SignWell document will be cancelled so the client can no longer sign.' : 'No SignWell cancellation needed.'}`)) {
      return;
    }
    setActionBusy(p => ({ ...p, [id]: true }));
    setActionStatus(p => ({ ...p, [id]: '' }));
    try {
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'void_letter',
          payload: {
            letterId: id,
            voidReason: voidReason || 'voided_by_rep',
            voidedBy: profile?.id || null,
            voidedByLabel: profile?.full_name || profile?.email || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      const swMsg = data.signwellCancel
        ? (data.signwellCancel.ok ? ' · SignWell document cancelled' : ` · ⚠ SignWell cancel failed: ${data.signwellCancel.error}`)
        : '';
      setActionStatus(p => ({ ...p, [id]: `✓ Voided${swMsg}` }));
      await load();
    } catch (e) {
      console.error('ETF void failed:', e);
      setActionStatus(p => ({ ...p, [id]: '✗ ' + (e.message || 'Void failed') }));
    } finally {
      setActionBusy(p => ({ ...p, [id]: false }));
    }
  }

  // Void this letter, then navigate to /etf-calculator with the values
  // pre-filled. The new letter created there will link back via
  // superseded_from. Use case: a client called to renegotiate, the originally
  // sent ETF is no longer accurate, void it and issue a fresh one.
  async function handleVoidAndReplace(row) {
    const id = row.id;
    if (!window.confirm(`Void this letter and start a new one with the same details?\n\nLetter: ${letterRefFor(row)}\nClient: ${row.client_name || '—'}\n\nThe voided letter will be marked superseded by the new one, and any active SignWell document will be cancelled.`)) {
      return;
    }
    setActionBusy(p => ({ ...p, [id]: true }));
    setActionStatus(p => ({ ...p, [id]: '' }));
    try {
      const res = await fetch('/.netlify/functions/etfDocument', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'void_letter',
          payload: {
            letterId: id,
            voidReason: 'replaced_by_new_letter',
            voidedBy: profile?.id || null,
            voidedByLabel: profile?.full_name || profile?.email || null,
          },
        }),
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.detail || data?.error || `HTTP ${res.status}`);
      }
      // Navigate to the calculator with the voided row's snapshot, so it
      // can prefill all the fields and stamp superseded_from on the new row.
      navigate('/etf-calculator', { state: { prefillFromVoid: row } });
    } catch (e) {
      console.error('ETF void-and-replace failed:', e);
      setActionStatus(p => ({ ...p, [id]: '✗ ' + (e.message || 'Action failed') }));
      setActionBusy(p => ({ ...p, [id]: false }));
    }
  }

  // Load Ferrum company info for letter rendering. Cached after first call.
  let _ferrumInfoCache = null;
  async function loadFerrumInfo() {
    if (_ferrumInfoCache) return _ferrumInfoCache;
    const { data } = await supabase
      .from('pricing_settings')
      .select('key, value')
      .in('key', ['company_name', 'company_address', 'company_phone', 'company_website', 'company_logo_url']);
    const map = {};
    for (const r of (data || [])) map[r.key] = r.value;
    _ferrumInfoCache = {
      name:    map.company_name    || 'Ferrum Technology Services',
      address: map.company_address || '',
      phone:   map.company_phone   || '',
      website: map.company_website || '',
      logoUrl: map.company_logo_url || '',
    };
    return _ferrumInfoCache;
  }

  // Client-side filters (status + search) — applied to the loaded set
  const filtered = useMemo(() => {
    let out = rows;
    if (statusFilter.size > 0) {
      out = out.filter(r => statusFilter.has(r.status));
    }
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      out = out.filter(r =>
        (r.client_name || '').toLowerCase().includes(term) ||
        (r.contract_reference || '').toLowerCase().includes(term) ||
        (r.hubspot_company_name || '').toLowerCase().includes(term)
      );
    }
    return out;
  }, [rows, statusFilter, search]);

  // Status counts (always against full loaded set, ignoring status filter,
  // so chips show how many would be available if you toggled them)
  const counts = useMemo(() => {
    const c = {};
    for (const r of rows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [rows]);

  function toggleStatus(s) {
    setStatusFilter(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  }

  function clearFilters() {
    setStatusFilter(new Set());
    setSearch('');
    setDateRange('all');
  }

  const anyFilterActive = statusFilter.size > 0 || search.trim() || dateRange !== 'all';

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto', flex: 1, background: v3.color.bg, fontFamily: v3.font.family }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: v3.font.size.pageTitle, fontWeight: v3.font.weight.semibold, color: v3.color.body, margin: 0, letterSpacing: v3.font.letterSpacing.tight }}>ETF Letters</h2>
          <p style={{ fontSize: v3.font.size.label, color: v3.color.muted, margin: '4px 0 0 0' }}>
            Every early-termination fee letter generated by the team — searchable and trackable.
          </p>
        </div>
        <button onClick={() => navigate('/etf-calculator')}
          style={{ padding: '9px 16px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: 'pointer' }}>
          + New ETF Letter
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.cardLarge, padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>Status</span>
          {STATUS_CHIPS.map(s => {
            const cfg = STATUS_STYLE[s];
            const active = statusFilter.has(s);
            const count = counts[s] || 0;
            return (
              <button key={s} type="button" onClick={() => toggleStatus(s)}
                style={{
                  padding: '3px 10px', borderRadius: v3.radius.pill, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold,
                  background: active ? cfg.color : cfg.bg,
                  color:      active ? 'white'    : cfg.color,
                  border: `1px solid ${active ? cfg.color : 'transparent'}`,
                  cursor: 'pointer',
                  opacity: count === 0 ? 0.4 : 1,
                }}>
                {cfg.label} {count > 0 && <span style={{ marginLeft: 2, opacity: 0.7 }}>({count})</span>}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>Date</span>
          {DATE_RANGES.map(r => (
            <button key={r.key} type="button" onClick={() => setDateRange(r.key)}
              style={{
                padding: '4px 10px', borderRadius: v3.radius.input, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold,
                background: dateRange === r.key ? v3.color.ink : 'transparent',
                color:      dateRange === r.key ? 'white'    : v3.color.muted,
                border: '1px solid ' + (dateRange === r.key ? v3.color.ink : v3.color.border),
                cursor: 'pointer',
              }}>
              {r.label}
            </button>
          ))}

          <div style={{ flex: 1, minWidth: 200 }}>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search client name or contract reference..."
              style={{ width: '100%', padding: v3.space.inputPad, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.body, outline: 'none', color: v3.color.body, background: v3.color.card }} />
          </div>

          {anyFilterActive && (
            <button type="button" onClick={clearFilters}
              style={{ padding: '4px 10px', fontSize: v3.font.size.small, color: v3.color.ink, background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: v3.font.weight.semibold }}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Result count */}
      <div style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft, marginBottom: 6 }}>
        {loading ? 'Loading…' : `${filtered.length} of ${rows.length} ETF letter${rows.length !== 1 ? 's' : ''}`}
        {error && <span style={{ color: v3.color.status.redText, marginLeft: 8 }}>· Error: {error}</span>}
      </div>

      {/* Table */}
      <div style={{ background: v3.color.card, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.cardLarge, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: v3.font.size.body }}>
          <thead>
            <tr style={{ background: v3.color.cardAlt, borderBottom: `1px solid ${v3.color.border}` }}>
              <Th>Date</Th>
              <Th>Client / Contract</Th>
              <Th align="right">Amount</Th>
              <Th>Status</Th>
              <Th>Last event</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r, i) => {
              const cfg = STATUS_STYLE[r.status] || STATUS_STYLE.draft;
              const ev = lastEvent(r);
              const isExpanded = expandedId === r.id;
              const isVoided = r.status === 'voided';
              return (
                <ETFLetterRow
                  key={r.id}
                  row={r}
                  cfg={cfg}
                  lastEv={ev}
                  rowIndex={i}
                  isExpanded={isExpanded}
                  isVoided={isVoided}
                  isEditing={editingId === r.id}
                  onToggle={() => {
                    if (editingId === r.id) return; // don't collapse while editing
                    setExpandedId(prev => prev === r.id ? null : r.id);
                  }}
                  onStartEdit={() => { setExpandedId(r.id); setEditingId(r.id); }}
                  onCancelEdit={() => setEditingId(null)}
                  onSaveEdit={(edits) => handleSaveEdit(r, edits)}
                  onVoid={() => handleVoid(r)}
                  onVoidAndReplace={() => handleVoidAndReplace(r)}
                  actionBusy={!!actionBusy[r.id]}
                  actionStatus={actionStatus[r.id] || ''}
                  navigate={navigate}
                />
              );
            })}
            {filtered.length === 0 && !loading && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center', color: v3.color.mutedSoft, fontSize: v3.font.size.body, fontStyle: 'italic' }}>
                  {anyFilterActive ? 'No ETF letters match your filters.' : 'No ETF letters yet. Generate one from the ETF Calculator or any saved quote.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ── Subcomponents ────────────────────────────────────────────────────────

function Th({ children, align = 'left' }) {
  return (
    <th style={{ textAlign: align, padding: v3.space.cellPad, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>
      {children}
    </th>
  );
}

function StatusBadge({ cfg, voided }) {
  return (
    <span style={{
      padding: '2px 8px', borderRadius: v3.radius.pill, fontSize: v3.font.size.micro, fontWeight: v3.font.weight.bold,
      background: cfg.bg, color: cfg.color,
      textDecoration: voided ? 'line-through' : 'none',
    }}>
      {cfg.label}
    </span>
  );
}

function ETFLetterRow({ row, cfg, lastEv, rowIndex, isExpanded, isVoided, isEditing,
                        onToggle, onStartEdit, onCancelEdit, onSaveEdit, onVoid, onVoidAndReplace,
                        actionBusy, actionStatus, navigate }) {
  const bg = isExpanded ? v3.color.inkBg : (rowIndex % 2 === 0 ? v3.color.card : v3.color.cardAlt);
  const letterRef = letterRefFor(row);

  return (
    <>
      <tr onClick={onToggle}
        style={{ borderBottom: `1px solid ${v3.color.borderSoft}`, background: bg, cursor: 'pointer', opacity: isVoided ? 0.6 : 1 }}
        onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.background = v3.color.inkBg; }}
        onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.background = rowIndex % 2 === 0 ? v3.color.card : v3.color.cardAlt; }}>
        <td style={{ padding: v3.space.cellPad, color: v3.color.bodySoft, whiteSpace: 'nowrap' }}>
          {fmtDateShort(row.created_at)}
        </td>
        <td style={{ padding: v3.space.cellPad }}>
          <div style={{ fontWeight: v3.font.weight.semibold, color: v3.color.body }}>
            {row.client_name || '—'}
            {row.quote_id && <span style={{ marginLeft: 6, fontSize: v3.font.size.micro, background: v3.color.status.blueBg, color: v3.color.status.blueText, padding: '1px 6px', borderRadius: v3.radius.pill, fontWeight: v3.font.weight.bold }}>Quote</span>}
            {!row.quote_id && <span style={{ marginLeft: 6, fontSize: v3.font.size.micro, background: v3.color.status.amberBg, color: v3.color.status.amberText, padding: '1px 6px', borderRadius: v3.radius.pill, fontWeight: v3.font.weight.bold }}>Ad-hoc</span>}
            {/* v3.5.99.7.19: letter ref next to badges */}
            {letterRef && <span style={{ marginLeft: 6, fontSize: v3.font.size.micro, fontFamily: v3.font.mono, color: v3.color.mutedSoft, fontWeight: v3.font.weight.semibold }}>{letterRef}</span>}
            {row.superseded_from && <span style={{ marginLeft: 6, fontSize: v3.font.size.micro, background: '#ede9fe', color: '#5b21b6', padding: '1px 6px', borderRadius: v3.radius.pill, fontWeight: v3.font.weight.bold }} title="This letter replaces an older voided letter">Replaces</span>}
          </div>
          {row.contract_reference && (
            <div style={{ fontSize: v3.font.size.micro, color: v3.color.muted, marginTop: 2 }}>Ref: {row.contract_reference}</div>
          )}
        </td>
        <td style={{ padding: v3.space.cellPad, textAlign: 'right', fontFamily: v3.font.mono, fontWeight: v3.font.weight.semibold, color: v3.color.body, whiteSpace: 'nowrap' }}>
          {fmt$(row.etf_amount)}
        </td>
        <td style={{ padding: v3.space.cellPad }}>
          <StatusBadge cfg={cfg} voided={isVoided} />
        </td>
        <td style={{ padding: v3.space.cellPad, color: v3.color.muted, fontSize: v3.font.size.small, whiteSpace: 'nowrap' }}>
          {lastEv.label} {fmtDateShort(lastEv.ts)}
        </td>
        <td style={{ padding: v3.space.cellPad, textAlign: 'right', whiteSpace: 'nowrap' }}>
          <span style={{ fontSize: v3.font.size.small, color: v3.color.mutedSoft }}>{isExpanded ? '▲' : '▼'}</span>
        </td>
      </tr>
      {isExpanded && (
        <ETFLetterDetailRow
          row={row}
          cfg={cfg}
          isEditing={isEditing}
          onStartEdit={onStartEdit}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
          onVoid={onVoid}
          onVoidAndReplace={onVoidAndReplace}
          actionBusy={actionBusy}
          actionStatus={actionStatus}
          navigate={navigate}
        />
      )}
    </>
  );
}

function ETFLetterDetailRow({ row, cfg, isEditing, onStartEdit, onCancelEdit, onSaveEdit,
                              onVoid, onVoidAndReplace, actionBusy, actionStatus, navigate }) {
  // Build timeline events
  const timeline = [
    { label: 'PDF generated',           ts: row.pdf_generated_at,    icon: '📄' },
    { label: 'Sent via SignWell',       ts: row.signwell_sent_at,    icon: '✉' },
    { label: 'Viewed by client',        ts: row.signwell_viewed_at,  icon: '👁' },
    { label: 'Signed by client',        ts: row.signwell_signed_at,  icon: '✓', color: '#166534' },
    { label: 'Declined by client',      ts: row.signwell_declined_at, icon: '✗', color: '#991b1b' },
    { label: 'Document expired',        ts: row.signwell_expired_at, icon: '⏰', color: '#92400e' },
    { label: 'HubSpot unsigned upload', ts: row.hubspot_unsigned_uploaded_at, icon: '🔗' },
    { label: 'HubSpot signed upload',   ts: row.hubspot_signed_uploaded_at,   icon: '🔗', color: '#166534' },
    { label: 'Voided',                  ts: row.voided_at,           icon: '⊘', color: '#991b1b' },
  ].filter(e => e.ts).sort((a, b) => new Date(a.ts) - new Date(b.ts));

  const letterRef = letterRefFor(row);
  const editable = isEditable(row);
  const replaceable = canVoidAndReplace(row);
  const voidable = canVoidOnly(row);

  return (
    <tr style={{ background: v3.color.inkBg, borderBottom: `1px solid ${v3.color.border}` }}>
      <td colSpan={6} style={{ padding: '16px 20px' }}>

        {/* v3.5.99.7.19: Letter UID header — copy button + status note */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, paddingBottom: 8, borderBottom: `1px solid ${v3.color.border}`, gap: 12 }}>
          <div>
            <div style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase }}>Letter ID</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
              <span style={{ fontFamily: v3.font.mono, fontSize: v3.font.size.label, fontWeight: v3.font.weight.bold, color: v3.color.body }}>{letterRef}</span>
              <span style={{ fontFamily: v3.font.mono, fontSize: v3.font.size.small, color: v3.color.mutedSoft }}>{row.id}</span>
              <button type="button"
                onClick={() => { navigator.clipboard?.writeText(row.id); }}
                title="Copy full UUID"
                style={{ padding: '2px 8px', fontSize: v3.font.size.micro, background: 'transparent', color: v3.color.ink, border: `1px solid ${v3.color.ink}`, borderRadius: v3.radius.input, cursor: 'pointer', fontWeight: v3.font.weight.semibold }}>
                Copy
              </button>
            </div>
          </div>
          {!editable && row.status !== 'voided' && (
            <div style={{ fontSize: v3.font.size.small, color: v3.color.status.amberText, background: v3.color.status.amberBg, padding: '4px 10px', borderRadius: v3.radius.input, fontWeight: v3.font.weight.semibold }}>
              🔒 Locked — sent for signature
            </div>
          )}
          {row.status === 'voided' && (
            <div style={{ fontSize: v3.font.size.small, color: v3.color.muted, background: v3.color.status.grayBg, padding: '4px 10px', borderRadius: v3.radius.input, fontWeight: v3.font.weight.semibold }}>
              ⊘ Voided{row.void_reason ? ` — ${row.void_reason}` : ''}
            </div>
          )}
        </div>

        {isEditing ? (
          <ETFLetterEditForm
            row={row}
            onCancel={onCancelEdit}
            onSave={onSaveEdit}
            busy={actionBusy}
          />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

            {/* Left: ETF Inputs + computed */}
            <div>
              <div style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, marginBottom: 6 }}>
                ETF inputs
              </div>
              <DetailRow label="MRR" value={fmt$(row.mrr)} />
              <DetailRow label="Term" value={row.term_months ? `${row.term_months} months` : '—'} />
              <DetailRow label="Contract start" value={fmtDateShort(row.contract_start_date)} />
              <DetailRow label="Termination date" value={fmtDateShort(row.termination_date)} />
              {row.months_remaining != null && (
                <DetailRow label="Months remaining" value={`${row.months_remaining} of ${row.term_months || '?'}`} />
              )}
              <DetailRow label="ETF amount" value={<strong style={{ color: v3.color.body, fontFamily: v3.font.mono }}>{fmt$(row.etf_amount)}</strong>} />
              {row.contract_reference && (
                <DetailRow label="Contract ref" value={row.contract_reference} />
              )}
              {row.client_contact_email && (
                <DetailRow label="Client contact" value={`${row.client_contact_name || ''} ${row.client_contact_email ? '<' + row.client_contact_email + '>' : ''}`.trim()} />
              )}
              {row.created_by_label && (
                <DetailRow label="Generated by" value={row.created_by_label} />
              )}
              {row.superseded_from && (
                <DetailRow label="Replaces" value={<span style={{ fontFamily: 'DM Mono, monospace', fontSize: 10 }}>ETF-{String(row.superseded_from).slice(0, 8)}</span>} />
              )}
            </div>

            {/* Right: Timeline */}
            <div>
              <div style={{ fontSize: v3.font.size.micro, fontWeight: v3.font.weight.semibold, color: v3.color.muted, textTransform: 'uppercase', letterSpacing: v3.font.letterSpacing.uppercase, marginBottom: 6 }}>
                Event timeline
              </div>
              {timeline.length === 0 ? (
                <div style={{ fontSize: v3.font.size.label, color: v3.color.mutedSoft, fontStyle: 'italic' }}>No events yet.</div>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {timeline.map((e, i) => (
                    <li key={i} style={{ fontSize: v3.font.size.label, color: e.color || v3.color.bodySoft, marginBottom: 4, display: 'flex', gap: 8 }}>
                      <span style={{ width: 18, flexShrink: 0 }}>{e.icon}</span>
                      <span style={{ flex: 1 }}>{e.label}</span>
                      <span style={{ color: v3.color.mutedSoft, fontSize: v3.font.size.small, whiteSpace: 'nowrap' }}>{fmtDateTime(e.ts)}</span>
                    </li>
                  ))}
                </ul>
              )}
              {row.decline_reason && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: v3.color.status.redBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.status.redText }}>
                  <strong>Decline reason:</strong> {row.decline_reason}
                </div>
              )}
              {row.internal_notes && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: v3.color.status.grayBg, borderRadius: v3.radius.input, fontSize: v3.font.size.small, color: v3.color.muted }}>
                  <strong>Notes:</strong> {row.internal_notes}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions row — only shown when not in edit mode */}
        {!isEditing && (
          <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${v3.color.border}`, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {row.hubspot_deal_id && (
              <a href={dealUrlFor(row.hubspot_deal_id)} target="_blank" rel="noopener noreferrer"
                style={{ padding: '6px 10px', background: '#ff7a59', color: 'white', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, textDecoration: 'none' }}>
                ↗ Open HubSpot deal
              </a>
            )}
            {row.signwell_url && ['sent', 'viewed'].includes(row.status) && (
              <a href={row.signwell_url} target="_blank" rel="noopener noreferrer"
                style={{ padding: '6px 10px', background: v3.color.ink, color: 'white', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, textDecoration: 'none' }}>
                ✍ Open in SignWell
              </a>
            )}
            {row.hubspot_signed_file_id && (
              <a href={dealUrlFor(row.hubspot_deal_id)} target="_blank" rel="noopener noreferrer"
                style={{ padding: '6px 10px', background: v3.color.status.greenText, color: 'white', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, textDecoration: 'none' }}>
                📄 Signed PDF on HubSpot
              </a>
            )}
            {row.hubspot_unsigned_file_id && !row.hubspot_signed_file_id && (
              <a href={dealUrlFor(row.hubspot_deal_id)} target="_blank" rel="noopener noreferrer"
                style={{ padding: '6px 10px', background: v3.color.muted, color: 'white', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, textDecoration: 'none' }}>
                📄 Unsigned PDF on HubSpot
              </a>
            )}
            {row.signwell_doc_id && (
              <span style={{ padding: '6px 10px', background: v3.color.status.grayBg, color: v3.color.muted, borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontFamily: v3.font.mono }}>
                SignWell: {row.signwell_doc_id.slice(0, 12)}…
              </span>
            )}
            <div style={{ flex: 1 }} />
            {/* v3.5.99.7.19 — edit/void controls */}
            {editable && (
              <button type="button" onClick={onStartEdit} disabled={actionBusy}
                style={{ padding: '6px 12px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, cursor: actionBusy ? 'not-allowed' : 'pointer' }}>
                ✎ Edit & Regenerate
              </button>
            )}
            {replaceable && (
              <button type="button" onClick={onVoidAndReplace} disabled={actionBusy}
                style={{ padding: '6px 12px', background: v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, cursor: actionBusy ? 'not-allowed' : 'pointer' }}>
                {actionBusy ? '⏳' : '↻ Void & Create New'}
              </button>
            )}
            {voidable && (
              <button type="button" onClick={onVoid} disabled={actionBusy}
                style={{ padding: '6px 12px', background: 'transparent', color: v3.color.status.redText, border: `1px solid ${v3.color.status.redText}`, borderRadius: v3.radius.input, fontSize: v3.font.size.small, fontWeight: v3.font.weight.semibold, cursor: actionBusy ? 'not-allowed' : 'pointer' }}>
                {actionBusy ? '⏳' : '⊘ Void'}
              </button>
            )}
          </div>
        )}

        {/* Inline action result */}
        {actionStatus && (
          <div style={{ marginTop: 8, fontSize: v3.font.size.small, color: actionStatus.startsWith('✓') ? v3.color.status.greenText : v3.color.status.redText }}>
            {actionStatus}
          </div>
        )}
      </td>
    </tr>
  );
}

// v3.5.99.7.19: inline edit form. Mounted only when isEditing is true. Uses
// EtfCalculatorBody so the editing UX matches the create UX. Save & Regenerate
// kicks back to handleSaveEdit on the page, which PATCHes the row + downloads
// the new PDF + refreshes the list.
function ETFLetterEditForm({ row, onCancel, onSave, busy }) {
  const [clientName,        setClientName]        = useState(row.client_name || '');
  const [clientAddress,     setClientAddress]     = useState(row.client_address || '');
  const [clientContact,     setClientContact]     = useState(row.client_contact_name || '');
  const [clientEmail,       setClientEmail]       = useState(row.client_contact_email || '');
  const [contractReference, setContractReference] = useState(row.contract_reference || '');
  const [mrr,               setMrr]               = useState(row.mrr != null ? String(row.mrr) : '');
  const [term,              setTerm]              = useState(row.term_months || 36);
  const [contractStart,     setContractStart]     = useState(row.contract_start_date || '');
  const [terminationDate,   setTerminationDate]   = useState(row.termination_date || new Date().toISOString().slice(0, 10));

  const calc = useMemo(() => calculateEtf({
    mrr,
    term,
    contractStart: contractStart ? new Date(contractStart) : null,
    terminationDate: terminationDate ? new Date(terminationDate) : null,
  }), [mrr, term, contractStart, terminationDate]);

  const canCalculate = mrr && term && contractStart && terminationDate && calc.warnings.length === 0;

  function handleSave() {
    onSave({
      clientName, clientAddress,
      clientContact, clientEmail,
      contractReference,
      mrr, term,
      contractStart, terminationDate,
    });
  }

  return (
    <div>
      <div style={{ marginBottom: 10, fontSize: v3.font.size.label, color: v3.color.muted }}>
        Editing <strong style={{ fontFamily: v3.font.mono, color: v3.color.body }}>{letterRefFor(row)}</strong> in place — the letter UID and HubSpot link stay the same, only the contents change. Save & Regenerate will produce a new PDF and re-upload it to the HubSpot deal.
      </div>
      <EtfCalculatorBody
        clientName={clientName} setClientName={setClientName}
        clientAddress={clientAddress} setClientAddress={setClientAddress}
        contractReference={contractReference} setContractReference={setContractReference}
        clientContact={clientContact} setClientContact={setClientContact}
        clientEmail={clientEmail} setClientEmail={setClientEmail}
        mrr={mrr} setMrr={setMrr}
        term={term} setTerm={setTerm}
        contractStart={contractStart} setContractStart={setContractStart}
        terminationDate={terminationDate} setTerminationDate={setTerminationDate}
        calc={calc}
        canCalculate={canCalculate}
        // We don't expose preview/PDF/HubSpot/SignWell buttons in the
        // edit context — Save & Regenerate replaces them.
        onGenerateLetter={() => {}}
        onPostToHubspot={null}
        onDownloadPdf={null}
        onSendViaSignwell={null}
        postingNote={false}
        postStatus=""
        generatingPdf={false}
        pdfStatus=""
        sendingSignwell={false}
        signwellStatus=""
        showContactFields={!!row.quote_id}
        hintMessage="Editing an unsent letter. Once saved, the row updates in place — same letter UID, fresh PDF."
      />
      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${v3.color.border}`, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ padding: '8px 14px', background: 'transparent', color: v3.color.muted, border: `1px solid ${v3.color.border}`, borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: busy ? 'not-allowed' : 'pointer' }}>
          Cancel
        </button>
        <button type="button" onClick={handleSave} disabled={busy || !canCalculate}
          style={{ padding: '8px 14px', background: (busy || !canCalculate) ? v3.color.mutedSoft : v3.color.ink, color: 'white', border: 'none', borderRadius: v3.radius.input, fontSize: v3.font.size.label, fontWeight: v3.font.weight.semibold, cursor: (busy || !canCalculate) ? 'not-allowed' : 'pointer' }}>
          {busy ? '⏳ Saving…' : '💾 Save & Regenerate'}
        </button>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4, fontSize: v3.font.size.label }}>
      <div style={{ width: 130, color: v3.color.muted, fontSize: v3.font.size.micro, textTransform: 'uppercase', letterSpacing: '.03em', flexShrink: 0 }}>{label}</div>
      <div style={{ color: v3.color.body, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}
